import React, { useEffect, useState } from 'react';
import { Modal } from '../../../components/organisms/Modal';
import { Button } from '../../../components/atoms';
import { FormField } from '../../../components/molecues/FormField';
import { resolveAssetUrl } from '../../../utils/url';
import type {
  Artist,
  ArtistStatus,
  CreateArtistRequest,
  UpdateArtistRequest,
} from '../../../types/api/artist';

interface ArtistFormState {
  name: string;
  bio: string;
  status: ArtistStatus;
}

const defaultFormState: ArtistFormState = {
  name: '',
  bio: '',
  status: 'ACTIVE',
};

export interface ArtistFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    data: CreateArtistRequest | UpdateArtistRequest,
    imageFile?: File | null
  ) => void | Promise<void>;
  mode: 'create' | 'edit';
  artist?: Artist;
}

export const ArtistFormModal: React.FC<ArtistFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  mode,
  artist,
}) => {
  const [formData, setFormData] = useState<ArtistFormState>(defaultFormState);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (mode === 'edit' && artist && isOpen) {
      setFormData({
        name: artist.name || '',
        bio: artist.bio || '',
        status: artist.status || 'ACTIVE',
      });
      setImagePreview(
        resolveAssetUrl(artist.imageUrl || artist.profileImageUrl || null) || null
      );
      setImageFile(null);
    } else if (isOpen) {
      setFormData(defaultFormState);
      setImagePreview(null);
      setImageFile(null);
    }
    setErrors({});
  }, [artist, mode, isOpen]);

  const validateImage = (file: File): string | null => {
    if (!file.type.startsWith('image/')) return 'Please select a valid image file';
    const maxSizeMb = 5;
    if (file.size > maxSizeMb * 1024 * 1024) return `Image size must be less than ${maxSizeMb}MB`;
    return null;
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const validationError = validateImage(file);
    if (validationError) {
      setErrors((prev) => ({ ...prev, image: validationError }));
      return;
    }
    setErrors((prev) => {
      const next = { ...prev };
      delete next.image;
      return next;
    });
    const reader = new FileReader();
    reader.onload = () => {
      setImageFile(file);
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const validate = (): boolean => {
    const nextErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      nextErrors.name = 'Artist name is required';
    } else if (formData.name.trim().length > 200) {
      nextErrors.name = 'Artist name must be less than 200 characters';
    }
    if (formData.bio.length > 2000) {
      nextErrors.bio = 'Bio must be less than 2000 characters';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validate()) return;

    const payload: CreateArtistRequest | UpdateArtistRequest = {
      name: formData.name.trim(),
      bio: formData.bio.trim() || undefined,
      status: formData.status,
    };

    setIsSubmitting(true);
    try {
      await onSubmit(payload, imageFile);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={mode === 'create' ? 'Add Artist' : 'Edit Artist'}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} isLoading={isSubmitting} type="submit">
            {mode === 'create' ? 'Create' : 'Update'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormField
          label="Artist Name"
          value={formData.name}
          onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
          error={errors.name}
          required
          placeholder="e.g., Sample Artist"
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Status</label>
          <select
            value={formData.status}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, status: e.target.value as ArtistStatus }))
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
          >
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Bio</label>
          <textarea
            rows={3}
            value={formData.bio}
            onChange={(e) => setFormData((prev) => ({ ...prev, bio: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
            placeholder="Optional artist bio"
          />
          {errors.bio ? <p className="text-red-500 text-xs mt-1">{errors.bio}</p> : null}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5 text-left">Image</label>
          <div className="w-28 h-28 rounded-full bg-gray-100 border flex items-center justify-center overflow-hidden mb-2">
            {imagePreview ? (
              <img src={imagePreview} alt="Artist preview" className="w-full h-full object-cover" />
            ) : (
              <span className="text-xs text-gray-400">No image</span>
            )}
          </div>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="block text-sm text-gray-700"
          />
          {errors.image ? <p className="text-sm text-red-600 mt-1">{errors.image}</p> : null}
        </div>
      </form>
    </Modal>
  );
};

export default ArtistFormModal;
